import { useEffect, useState, useRef, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { fetchData, SeenNoteAction } from "../../redux/slices/mainDataSlice";
import { toast } from "react-hot-toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faTrashAlt,
  faEnvelopeOpenText,
  faReply,
  faCheckCircle,
  faTimesCircle,
  faSchool,
} from "@fortawesome/free-solid-svg-icons";
import "../../main.scss";
import Can from "../../permissions/Can";
import Tooltip from "../../components/Tooltip";

export default function SchoolNotes({ embedded, rgmw }) {
  const dispatch = useDispatch();
  const notesData = useSelector((state) => state.main.data?.alfarestext || []);
  const schools = useSelector((state) => state.main.data?.schoolData || []);
  const loading = useSelector((state) => state.main.loading);

  const notes = useMemo(() => {
    if (embedded && rgmw) {
      return notesData.filter((n) => String(n.school_rgmw) === String(rgmw));
    }
    return notesData;
  }, [notesData, embedded, rgmw]);

  const [selectedNotes, setSelectedNotes] = useState(new Set());
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [showSchoolsModal, setShowSchoolsModal] = useState(false);
  const [animateIn, setAnimateIn] = useState(false);
  const [animateInSchools, setAnimateInSchools] = useState(false);
  const [replyTarget, setReplyTarget] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedSchools, setSelectedSchools] = useState(new Set());
  const [schoolsSearchQuery, setSchoolsSearchQuery] = useState("");

  const textareaRef = useRef(null);

  useEffect(() => {
    if (!notes?.length) dispatch(fetchData());
  }, [dispatch, notes?.length]);

  useEffect(() => {
    if (showReplyModal || showSchoolsModal) {
      requestAnimationFrame(() => setAnimateIn(true));
    } else {
      setAnimateIn(false);
    }
  }, [showReplyModal, showSchoolsModal]);

  useEffect(() => {
    if (showSchoolsModal) {
      requestAnimationFrame(() => setAnimateInSchools(true));
    } else {
      setAnimateInSchools(false);
    }
  }, [showSchoolsModal]);

  useEffect(() => {
    if (showReplyModal && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [showReplyModal]);

  const getSchoolName = (rgmw) => {
    const s = schools.find((x) => String(x.rgmw) === String(rgmw));
    return s?.schoolname || String(rgmw);
  };

  const toggleSelectNote = (id) => {
    setSelectedNotes((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleDeleteSelected = async () => {
    if (selectedNotes.size === 0) return;

    toast(
      (t) => (
        <div className="confirm-toast">
          <p>هل أنت متأكد من حذف {selectedNotes.size} رسائل؟</p>
          <div className="toast-actions">
            <button
              onClick={async () => {
                const l = toast.loading("جارٍ الحذف...");
                try {
                  await Promise.all(
                    Array.from(selectedNotes).map((id) =>
                      dispatch(SeenNoteAction(id))
                    )
                  );
                  toast.success("تم الحذف بنجاح");
                  setSelectedNotes(new Set());
                  dispatch(fetchData());
                } catch {
                  toast.error("فشل الحذف");
                } finally {
                  toast.dismiss(l);
                  toast.dismiss(t.id);
                }
              }}
              className="btn-confirm"
            >
              نعم، احذف
            </button>
            <button onClick={() => toast.dismiss(t.id)} className="btn-cancel">
              إلغاء
            </button>
          </div>
        </div>
      ),
      { duration: 6000 }
    );
  };

  const handleOpenReply = (note) => {
    setReplyTarget({
      id: note.id,
      rgmw: note.school_rgmw,
      schoolname: getSchoolName(note.school_rgmw),
      fares_replay: note.fares_replay || "",
    });
    setReplyText(note.fares_replay || "");
    setShowReplyModal(true);
  };

  const handleSendReply = async () => {
    if (!replyText.trim()) {
      toast.error("يرجى إدخال نص الرسالة");
      return;
    }

    // إذا كانت رسالة مخصصة، اعرض نافذة اختيار المدارس
    if (!replyTarget.id) {
      setShowReplyModal(false);
      setShowSchoolsModal(true);
      return;
    }

    const l = toast.loading("جارٍ إرسال الرسالة...");
    try {
      const credentials = JSON.parse(localStorage.getItem("userCredentials"));
      const token = credentials?.token;

      const response = await fetch(
        `https://api.alfares.cloud/api/alfares/notes/reply/${replyTarget.id}/${token}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ reply: replyText.trim() }),
        }
      );

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.message || "فشل في إرسال الرسالة");
      }
      toast.success(`تم إرسال الرد بنجاح`, { id: l });
      setShowReplyModal(false);
      setReplyTarget(null);
      setReplyText("");
      dispatch(fetchData());
    } catch {
      toast.error("فشل في إرسال الرسالة", { id: l });
    }
  };

  const handleSendCustomMessage = async () => {
    if (selectedSchools.size === 0) {
      toast.error("يرجى اختيار مدرسة واحدة على الأقل");
      return;
    }

    const l = toast.loading("جارٍ تحديث المدارس...");
    try {
      const credentials = JSON.parse(localStorage.getItem("userCredentials"));
      const token = credentials?.token;

      // تحديث حقل noteforschool لكل مدرسة محددة
      const updatePromises = Array.from(selectedSchools).map((rgmw) =>
        fetch(
          `https://api.alfares.cloud/api/alfares/schools/updateNote/${rgmw}/${token}`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ noteforschool: replyText.trim() }),
          }
        )
      );

      const results = await Promise.all(updatePromises);

      // التحقق من نجاح جميع الطلبات
      const failedUpdates = results.filter((res) => !res.ok);
      if (failedUpdates.length > 0) {
        throw new Error(`فشل في تحديث ${failedUpdates.length} مدارس`);
      }

      toast.success(`تم تحديث ${selectedSchools.size} مدرسة بنجاح`, { id: l });
      setShowSchoolsModal(false);
      setSelectedSchools(new Set());
      setReplyText("");
      setReplyTarget(null);
      dispatch(fetchData());
    } catch (error) {
      toast.error(error.message || "فشل في تحديث المدارس", { id: l });
    }
  };

  const handleMarkAsSeen = async (noteId) => {
    const l = toast.loading("تحديث الحالة...");
    try {
      const credentials = JSON.parse(localStorage.getItem("userCredentials"));
      const token = credentials?.token;

      const response = await fetch(
        `https://api.alfares.cloud/api/alfares/notes/seen/${noteId}/${token}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
        }
      );

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.message || "فشل في تحديث الحالة");
      }

      toast.success("تمت المشاهدة", { id: l });
      dispatch(fetchData());
    } catch {
      toast.error("فشل التحديث", { id: l });
    }
  };

  const filteredNotes = notes.filter((n) =>
    getSchoolName(n.school_rgmw)
      .toLowerCase()
      .includes(searchQuery.toLowerCase())
  );

  const closeModals = () => {
    setShowReplyModal(false);
    setShowSchoolsModal(false);
    setReplyTarget(null);
    setReplyText("");
    setSelectedSchools(new Set());
  };

  const toggleSelectSchool = (rgmw) => {
    setSelectedSchools((prev) => {
      const next = new Set(prev);
      if (next.has(rgmw)) next.delete(rgmw);
      else next.add(rgmw);
      return next;
    });
  };

  const filteredSchools = schools.filter((s) =>
    s.schoolname?.toLowerCase().includes(schoolsSearchQuery.toLowerCase())
  );

  return (
    <div
      className={`transferred-wrapper anim-fade-in ${
        embedded ? "embedded-mode" : ""
      }`}
    >
      <div
        className="premium-edit-container"
        style={{ maxWidth: embedded ? "100%" : "1280px" }}
      >
        {/* Premium Compact Header */}
        {!embedded && (
          <div className="premium-header-modern">
            <div className="header-left">
              <div className="header-text-box">
                <h2>رسائل من المستخدمين</h2>
                <div className="breadcrumb-info">
                  <span>نظام الفارس</span>
                  <span className="separator">/</span>
                  <span className="current-page">إدارة الرسائل والملاحظات</span>
                </div>
              </div>
            </div>

            <div className="header-tools">
              <div className="global-search-container">
                <FontAwesomeIcon icon={faSearch} className="s-icon" />
                <input
                  type="text"
                  placeholder="ابحث عن مدرسة..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              <Can module="school_notes" action="add">
                <button
                  className="custom-message-btn"
                  onClick={() => {
                  setReplyTarget({
                    id: null,
                    rgmw: null,
                    schoolname: "رسالة مخصصة",
                    fares_replay: "",
                  });
                  setReplyText("");
                  setShowReplyModal(true);
                }}
              >
                <FontAwesomeIcon icon={faSchool} />
                <span>رسالة مخصصة</span>
              </button>
              </Can>

              <div className="results-count">
                <b>{filteredNotes.length}</b> رسالة
              </div>


              <Can module="school_notes" action="delete">
                <button
                  className={`bulk-delete-btn ${
                    selectedNotes.size > 0 ? "show" : ""
                  }`}
                  onClick={handleDeleteSelected}
                >
                  <FontAwesomeIcon icon={faTrashAlt} />
                  <span>حذف المحدد ({selectedNotes.size})</span>
                </button>
              </Can>
            </div>
          </div>
        )}

        {/* Table Content */}
        <div className="premium-table-container">
          {loading ? (
            <div className="loader-overlay">
              <div className="premium-loader"></div>
              <span>جارٍ جلب الرسائل...</span>
            </div>
          ) : (
            <table className="modern-condensed-table">
              <thead>
                <tr>
                  <th width="40" className="check-header">
                    <input
                      type="checkbox"
                      onChange={(e) => {
                        if (e.target.checked)
                          setSelectedNotes(
                            new Set(filteredNotes.map((n) => n.id))
                          );
                        else setSelectedNotes(new Set());
                      }}
                      checked={
                        selectedNotes.size === filteredNotes.length &&
                        filteredNotes.length > 0
                      }
                    />
                  </th>
                  <th width="50" className="idx-header">
                    #
                  </th>
                  <th className="school-header">المدرسة</th>
                  <th className="msg-header">الرسالة</th>
                  <th className="date-header">التاريخ</th>
                  <th width="200" className="actions-header">
                    الإجراءات
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredNotes.length > 0 ? (
                  filteredNotes.map((note, idx) => {
                    const isSelected = selectedNotes.has(note.id);
                    const isUnread = note.has_seen === 0;
                    return (
                      <tr
                        key={note.id}
                        className={`${isSelected ? "row-selected" : ""} ${
                          isUnread ? "row-unread" : ""
                        }`}
                      >
                        <td className="check-cell">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleSelectNote(note.id)}
                          />
                        </td>
                        <td
                          className="idx-cell"
                          style={{
                            color: "var(--muted)",
                            fontWeight: 700,
                            textAlign: "center",
                          }}
                        >
                          {idx + 1}
                        </td>
                        <td className="info-cell">
                          <div className="meta-minimal">
                            <span className="name">
                              {getSchoolName(note.school_rgmw)}
                            </span>
                            <p
                              className={`note-text mobile-note-text ${
                                isUnread ? "unread" : ""
                              }`}
                            >
                              {note.notetext}
                            </p>
                          </div>
                        </td>
                        <td className="message-cell msg-col">
                          <p
                            className={`note-text ${isUnread ? "unread" : ""}`}
                          >
                            {note.notetext}
                          </p>
                        </td>
                        <td className="date-col">
                          <div className="type-badge date-badge">
                            {note.submission_datetime || "-"}
                          </div>
                        </td>
                        <td className="actions-cell">
                          <div className="action-buttons">
                            <Can module="school_notes" action="edit">
                              <button
                                onClick={() => handleOpenReply(note)}
                                className={`action-icon-btn ${
                                  note.fares_replay ? "replied" : "reply"
                                }`}
                              title={
                                note.fares_replay ? "مشاهدة الرد" : "إضافة رد"
                              }
                            >
                              <FontAwesomeIcon icon={faReply} />
                            </button>
                            </Can>
                            <Can module="school_notes" action="edit">
                              <Tooltip content="تمت المشاهدة">
                                <button
                                  onClick={() => handleMarkAsSeen(note.id)}
                                  className={`action-icon-btn seen ${
                                  note.has_seen === 1 ? "disabled" : ""
                                }`}
                                disabled={note.has_seen === 1}
                             
                              >
                                <FontAwesomeIcon
                                  icon={
                                    note.has_seen === 1
                                      ? faCheckCircle
                                      : faEnvelopeOpenText
                                  }
                                />
                              </button>
                              </Tooltip>
                            </Can>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="6" className="empty-ui-state">
                      <FontAwesomeIcon
                        icon={faEnvelopeOpenText}
                        className="icon"
                      />
                      <h3>لا توجد رسائل مطابقة</h3>
                      <p>جرّب استخدام كلمات بحث مختلفة</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Reply Modal */}
      {showReplyModal && (
        <div className={`premium-modal-overlay ${animateIn ? "active" : ""}`}>
          <div className="premium-modal-card">
            <div className="modal-header notes-modal-header">
              <div className="icon">
                <FontAwesomeIcon icon={faReply} />
              </div>
              <div className="text">
                <h3>{replyTarget?.id ? "الرد على الرسالة" : "رسالة مخصصة"}</h3>
                <p>{replyTarget?.schoolname}</p>
              </div>
            </div>

            <div className="modal-body">
              <div className="input-field">
                <label>{replyTarget?.id ? "نص الرد" : "نص الرسالة"}</label>
                <div className="input-wrapper">
                  <textarea
                    ref={textareaRef}
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder={replyTarget?.id ? "اكتب ردك هنا..." : "اكتب رسالتك المخصصة هنا..."}
                    className="premium-textarea"
                  />
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <Can module="school_notes" action="edit">
                <button
                  onClick={handleSendReply}
                  className="premium-submit-btn notes-submit"
                disabled={
                  replyTarget?.id &&
                  replyTarget?.fares_replay &&
                  replyTarget?.fares_replay.trim() !== ""
                }
              >
                <div className="btn-content">
                  <FontAwesomeIcon icon={faCheckCircle} />
                  <span>
                    {replyTarget?.id && replyTarget?.fares_replay
                      ? "تم الرد مسبقاً"
                      : "التالي"}
                  </span>
                </div>
                <div className="btn-glow"></div>
              </button>
              </Can>
              <button onClick={closeModals} className="cancel-btn">
                <FontAwesomeIcon icon={faTimesCircle} />
                <span>إلغاء</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Schools Selection Modal */}
      {showSchoolsModal && (
        <div className={`premium-modal-overlay ${animateInSchools ? "active" : ""}`}>
          <div className="premium-modal-card schools-modal">
            <div className="modal-header notes-modal-header">
              <div className="icon">
                <FontAwesomeIcon icon={faSchool} />
              </div>
              <div className="text">
                <h3>اختر المدارس</h3>
                <p>حدد المدارس التي تريد إرسال الرسالة إليها</p>
              </div>
            </div>

            <div className="modal-body">
              <div className="input-field">
                <label>البحث عن مدرسة</label>
                <div className="input-wrapper">
                  <input
                    type="text"
                    placeholder="ابحث عن مدرسة..."
                    value={schoolsSearchQuery}
                    onChange={(e) => setSchoolsSearchQuery(e.target.value)}
                    className="premium-textarea"
                    style={{ minHeight: "44px", resize: "none" }}
                  />
                </div>
              </div>

              <div className="schools-list">
                <div className="schools-header">
                  <label>
                    <input
                      type="checkbox"
                      onChange={(e) => {
                        if (e.target.checked)
                          setSelectedSchools(new Set(filteredSchools.map((s) => s.rgmw)));
                        else setSelectedSchools(new Set());
                      }}
                      checked={
                        selectedSchools.size === filteredSchools.length &&
                        filteredSchools.length > 0
                      }
                    />
                    <span>تحديد الكل</span>
                  </label>
                  <span className="selected-count">
                    {selectedSchools.size} مدرسة محددة
                  </span>
                </div>

                <div className="schools-items">
                  {filteredSchools.map((school) => (
                    <label key={school.rgmw} className="school-item">
                      <input
                        type="checkbox"
                        checked={selectedSchools.has(school.rgmw)}
                        onChange={() => toggleSelectSchool(school.rgmw)}
                      />
                      <span className="school-name">{school.schoolname}</span>
                      <span className="school-rgmw">{school.rgmw}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <Can module="school_notes" action="add">
                <button
                  onClick={handleSendCustomMessage}
                  className="premium-submit-btn notes-submit"
                disabled={selectedSchools.size === 0}
              >
                <div className="btn-content">
                  <FontAwesomeIcon icon={faCheckCircle} />
                  <span>إرسال الرسالة</span>
                </div>
                <div className="btn-glow"></div>
              </button>
              </Can>
              <button onClick={closeModals} className="cancel-btn">
                <FontAwesomeIcon icon={faTimesCircle} />
                <span>إلغاء</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .transferred-wrapper {
          padding: 5px;
          color: var(--text);
          font-family: 'Cairo', sans-serif;
          font-weight: 400;
        }
        .embedded-mode { padding: 0; }

        .premium-edit-container {
          background: transparent;
          border: none;
          border-radius: 0;
          margin: 0;
          overflow: visible;
          box-shadow: none;
          width: 100%;
          position: relative;
        }

        .premium-header-modern {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 30px;
          background: rgba(255, 255, 255, 0.7);
          backdrop-filter: blur(10px);
          border: 1px solid var(--border);
          border-radius: 24px 24px 0 0;
          gap: 20px;
        }

        .premium-table-container {
          padding: 15px;
          background: rgba(255, 255, 255, 0.5);
          backdrop-filter: blur(10px);
          border: 1px solid var(--border);
          border-top: none;
          border-radius: 0 0 24px 24px;
        }
        .header-icon-box {
          width: 48px; height: 48px;
          background: var(--accent);
          border-radius: 14px;
          display: flex; align-items: center; justify-content: center;
          font-size: 20px; color: white;
        }
        .header-icon-box.notes-icon { background: linear-gradient(135deg, #28917f, #1d7567); }

        .header-text-box h2 { margin: 0; font-size: 20px; font-weight: 400; }
        .breadcrumb-info { display: flex; align-items: center; gap: 4px; color: var(--muted); font-size: 12px; margin-top: 2px; }
        .breadcrumb-info span:first-child { color: #28917f; font-weight: 400; }
        .current-page { color: #1d7567; font-weight: 400; }

        .header-tools { display: flex; align-items: center; gap: 10px; flex: 1; justify-content: flex-end; }

        .global-search-container {
          background: var(--bg);
          border: 1px solid var(--border);
          border-radius: 12px;
          padding: 0 15px;
          display: flex;
          align-items: center;
          gap: 12px;
          width: 100%;
          max-width: 300px;
          height: 44px;
          transition: all 0.3s;
        }
        .global-search-container:focus-within { border-color: #28917f; box-shadow: 0 0 0 3px rgba(40, 145, 127, 0.1); }
        .s-icon { color: #28917f; }
        .global-search-container input { background: transparent; border: none; color: var(--text); font-size: 14px; width: 100%; outline: none; font-family: 'Cairo', sans-serif; font-weight: 400; }

        .results-count {
          background: var(--panel-2);
          padding: 0 15px;
          border-radius: 12px;
          font-size: 13px;
          color: var(--muted);
          border: 1px solid var(--border);
          display: flex;
          align-items: center;
          gap: 6px;
          height: 44px;
          white-space: nowrap;
          transition: all 0.3s;
          font-family: 'Cairo', sans-serif;
          font-weight: 400;
        }
        .results-count:hover { border-color: #28917f; background: rgba(40, 145, 127, 0.05); }
        .results-count b { color: #28917f; font-size: 15px; font-weight: 400; }

        .bulk-delete-btn {
          background: #ef4444; color: white; border: none; border-radius: 12px;
          display: flex; align-items: center; gap: 8px;
          font-size: 13px; font-weight: 400; cursor: pointer;
          opacity: 0; pointer-events: none; transform: scale(0.8); transition: 0.3s;
          max-width: 0; padding: 0; margin: 0; overflow: hidden; white-space: nowrap;
          font-family: 'Cairo', sans-serif;
        }
        .bulk-delete-btn.show { opacity: 1; pointer-events: auto; transform: scale(1); max-width: 300px; padding: 10px 18px; }

        .custom-message-btn {
          background: linear-gradient(135deg, #10b981, #059669);
          color: white;
          border: none;
          border-radius: 12px;
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 13px;
          font-weight: 400;
          cursor: pointer;
          padding: 10px 18px;
          transition: all 0.3s;
          white-space: nowrap;
          font-family: 'Cairo', sans-serif;
        }
        .custom-message-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
        }

        .premium-table-container { padding: 15px 20px 20px; }

        /* تنسيق جدول رسائل المستخدمين بنفس تنسيق جدول حركات المعلمين */
        .modern-condensed-table {
          width: 100%;
          border-collapse: collapse;
          text-align: right;
          font-size: 13px;
          table-layout: auto;
          direction: rtl;
          font-family: 'Cairo', sans-serif;
          font-weight: 400;
        }
        .modern-condensed-table th {
          background: var(--panel-2);
          padding: 10px 12px;
          color: var(--accent);
          font-weight: 400;
          border-bottom: 1px solid var(--border);
          white-space: nowrap;
        }
        .modern-condensed-table td {
          padding: 8px 12px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.05);
          color: var(--text);
          white-space: nowrap;
          font-weight: 400;
        }
        .modern-condensed-table tr:hover {
          background: rgba(255, 255, 255, 0.02);
        }
        .modern-condensed-table tr.row-selected {
          background: rgba(239, 68, 68, 0.05);
        }
        .modern-condensed-table tr.row-unread {
          background: rgba(40, 145, 127, 0.05);
        }

        /* تقليل المسافة بين رقم التسلسل واسم المدرسة */
        .modern-condensed-table td:nth-child(2),
        .modern-condensed-table th:nth-child(2),
        .modern-condensed-table td:nth-child(3),
        .modern-condensed-table th:nth-child(3) {
          padding-left: 6px;
          padding-right: 6px;
        }

        /* توسيع مساحة حقل الرسالة */
        .message-cell {
          max-width: none;
        }
        .note-text {
          margin: 0;
          font-size: 14px;
          color: var(--muted);
          line-height: 1.5;
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
          font-family: 'Cairo', sans-serif;
          font-weight: 400;
        }
        .note-text.unread { color: var(--text); font-weight: 400; }

        /* إزالة البولد عن اسم المدرسة */
        .meta-minimal .name {
          font-weight: 400;
          font-size: 15px;
          color: var(--text);
        }

        /* محاذاة عمود الإجراءات لليسار */
        .modern-condensed-table td.actions-cell,
        .modern-condensed-table th.actions-header {
          text-align: left;
        }

        .info-cell {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .action-buttons { display: flex; gap: 8px; }
        .action-icon-btn {
          width: 36px; height: 36px; border-radius: 10px; border: 1px solid var(--border);
          background: var(--panel-2); color: var(--muted); cursor: pointer; transition: 0.2s;
          display: flex; align-items: center; justify-content: center; font-size: 14px;
        }
        .action-icon-btn:hover { border-color: #28917f; color: #28917f; transform: translateY(-2px); }
        .action-icon-btn.replied { background: rgba(16, 185, 129, 0.1); color: #10b981; border-color: rgba(16, 185, 129, 0.2); }
        .action-icon-btn.seen:hover { border-color: #10b981; color: #10b981; }
        .action-icon-btn.disabled { opacity: 0.3; cursor: not-allowed; pointer-events: none; }

        .premium-loader { width: 30px; height: 30px; border: 2px solid rgba(40, 145, 127, 0.1); border-top-color: #28917f; border-radius: 50%; animation: spin 1s infinite linear; margin: 0 auto 10px; }
        @keyframes spin { to { transform: rotate(360deg); } }

        /* Modal */
        .premium-modal-overlay {
          position: fixed; top: 0; left: 0; width: 100%; height: 100%;
          background: rgba(13, 35, 32, 0.45); backdrop-filter: blur(10px);
          display: flex; align-items: center; justify-content: center; z-index: 10000;
          opacity: 0; pointer-events: none; transition: 0.4s;
        }
        .premium-modal-overlay.active { opacity: 1; pointer-events: auto; }
        .premium-modal-card {
          background: var(--panel); border: 1px solid var(--border); width: 100%; max-width: 500px;
          border-radius: 24px; padding: 30px;
          transform: translateY(30px) scale(0.95); transition: 0.4s;
        }
        .premium-modal-card.schools-modal {
          max-width: 700px;
          max-height: 80vh;
          overflow-y: auto;
        }
        .premium-modal-overlay.active .premium-modal-card { transform: translateY(0) scale(1); }

        .modal-header { display: flex; align-items: center; gap: 20px; margin-bottom: 25px; }
        .notes-modal-header .icon {
          width: 50px; height: 50px; background: rgba(40, 145, 127, 0.12);
          border-radius: 14px; display: flex; align-items: center; justify-content: center;
          font-size: 22px; color: #28917f;
        }
        .modal-header h3 { margin: 0; font-size: 20px; font-weight: 400; font-family: 'Cairo', sans-serif; }
        .modal-header p { margin: 5px 0 0; color: var(--muted); font-size: 14px; font-family: 'Cairo', sans-serif; font-weight: 400; }

        .premium-textarea {
          width: 100%; min-height: 150px; background: var(--panel-2); border: 1px solid var(--border);
          border-radius: 12px; padding: 15px; color: var(--text); font-family: 'Cairo', sans-serif;
          font-size: 15px; transition: 0.3s; outline: none; resize: vertical; font-weight: 400;
        }
        .premium-textarea:focus { border-color: #28917f; box-shadow: 0 0 0 4px rgba(40, 145, 127, 0.15); }

        .notes-submit { flex: 2; background: linear-gradient(135deg, #28917f 0%, #1d7567 100%); color: white; border: none; height: 52px; border-radius: 12px; font-weight: 400; cursor: pointer; position: relative; overflow: hidden; transition: 0.3s; pointer-events: auto; font-family: 'Cairo', sans-serif; }
        .notes-submit:hover { transform: translateY(-2px); filter: brightness(1.1); }
        .notes-submit:disabled { opacity: 0.6; cursor: not-allowed; pointer-events: none; }
        .btn-content { display: flex; align-items: center; justify-content: center; gap: 10px; width: 100%; height: 100%; pointer-events: none; }
        .btn-glow { position: absolute; top: 0; left: 0; width: 100%; height: 100%; pointer-events: none; }

        .cancel-btn { flex: 1; background: var(--panel-2); border: 1px solid var(--border); color: var(--text); border-radius: 12px; display: flex; align-items: center; justify-content: center; gap: 10px; font-weight: 400; cursor: pointer; transition: 0.2s; height: 52px; font-family: 'Cairo', sans-serif; }
        .cancel-btn:hover { border-color: #ef4444; color: #ef4444; }

        .confirm-toast { text-align: center; font-family: 'Cairo', sans-serif; font-weight: 400; }
        .toast-actions { display: flex; justify-content: center; gap: 12px; margin-top: 18px; }
        .btn-confirm { background: #ef4444; color: white; border: none; padding: 10px 20px; border-radius: 10px; cursor: pointer; font-weight: 400; font-family: 'Cairo', sans-serif; }
        .btn-cancel { background: var(--panel-2); border: 1px solid var(--border); color: var(--text); padding: 10px 20px; border-radius: 10px; cursor: pointer; font-weight: 400; font-family: 'Cairo', sans-serif; }

        /* Schools Selection Modal Styles */
        .schools-list {
          margin-top: 20px;
          border: 1px solid var(--border);
          border-radius: 12px;
          background: var(--panel-2);
          overflow: hidden;
        }

        .schools-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px 15px;
          background: rgba(40, 145, 127, 0.1);
          border-bottom: 1px solid var(--border);
        }

        .schools-header label {
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          font-weight: 400;
          color: var(--text);
          font-family: 'Cairo', sans-serif;
        }

        .schools-header input[type="checkbox"] {
          width: 18px;
          height: 18px;
          cursor: pointer;
        }

        .selected-count {
          font-size: 13px;
          color: #28917f;
          font-weight: 400;
          font-family: 'Cairo', sans-serif;
        }

        .schools-items {
          max-height: 300px;
          overflow-y: auto;
          padding: 10px;
        }

        .school-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 10px 12px;
          border-radius: 8px;
          cursor: pointer;
          transition: background 0.2s;
          margin-bottom: 4px;
        }

        .school-item:hover {
          background: rgba(40, 145, 127, 0.05);
        }

        .school-item input[type="checkbox"] {
          width: 18px;
          height: 18px;
          cursor: pointer;
        }

        .school-name {
          flex: 1;
          font-size: 14px;
          color: var(--text);
          font-weight: 400;
          font-family: 'Cairo', sans-serif;
        }

        .school-rgmw {
          font-size: 12px;
          color: var(--muted);
          background: var(--panel);
          padding: 4px 8px;
          border-radius: 6px;
          font-family: 'Cairo', sans-serif;
          font-weight: 400;
        }

        .schools-items::-webkit-scrollbar {
          width: 8px;
        }

        .schools-items::-webkit-scrollbar-track {
          background: var(--panel-2);
          border-radius: 4px;
        }

        .schools-items::-webkit-scrollbar-thumb {
          background: #28917f;
          border-radius: 4px;
        }

        @media (max-width: 1000px) {
          .premium-header-modern { flex-direction: column; align-items: stretch; padding: 20px; }
          .header-left { justify-content: center; margin-bottom: 15px; }
          .header-tools { flex-direction: column; width: 100%; }
          .global-search-container { max-width: 100%; }
        }

        @media (max-width: 768px) {
          .global-search-container, .results-count { display: none !important; }
          .modern-condensed-table, .modern-condensed-table tbody, .modern-condensed-table tr { display: flex !important; flex-wrap: wrap !important; width: 100% !important; }
          .modern-condensed-table tbody { gap: 10px; }
          .modern-condensed-table thead { display: none !important; }
          .modern-condensed-table tr {
            flex-direction: row; align-items: flex-start; justify-content: flex-start;
            padding: 12px; background: var(--panel);
            border-radius: 6px; border: 1px solid var(--border) !important;
            box-shadow: 0 2px 8px rgba(0,0,0,0.04);
            margin-bottom: 0 !important; gap: 0;
          }
          .modern-condensed-table td { display: block; border: none !important; padding: 0 !important; border-radius: 0 !important; }

          /* سطر علوي: تشيك + اسم المدرسة + أزرار */
          .check-cell { order: 1; flex-shrink: 0; align-self: center; margin-inline-end: 8px; }
          .idx-cell { display: none !important; }
          .info-cell { order: 2; flex: 1; min-width: 0; align-self: center; }
          .actions-cell { order: 3; flex-shrink: 0; align-self: center; margin-inline-start: 8px; }
          /* التاريخ سطر ثاني */
          .date-col { order: 4; flex-basis: 100%; width: 100%; margin-top: 8px; }
          /* الرسالة سطر ثالث */
          .message-cell.msg-col { order: 5; width: 100% !important; flex-basis: 100%; margin-top: 8px; padding-top: 10px !important; border-top: 1px dashed var(--border) !important; }

          .info-cell .notes-avatar { display: none !important; }
          .meta-minimal { width: 100%; }
          .meta-minimal .name {
            font-size: 15px !important; font-weight: 400;
            white-space: normal !important; overflow: visible !important; text-overflow: clip !important;
            display: block; color: var(--text); line-height: 1.5;
          }
          .mobile-note-text { display: none !important; }

          .message-cell.msg-col .note-text { font-size: 13px !important; text-align: right; color: var(--text); line-height: 1.7; display: block; background: var(--panel-2); padding: 10px 12px; border-radius: 6px; border: 1px solid var(--border); }
          .message-cell.msg-col .note-text.unread { color: var(--text); font-weight: 400; background: color-mix(in srgb, var(--accent) 8%, transparent); border-color: color-mix(in srgb, var(--accent) 20%, transparent); }

          .date-badge { font-size: 11px !important; white-space: nowrap; padding: 4px 8px; border-radius: 6px; background: var(--panel-2); border: 1px solid var(--border); color: var(--muted); display: inline-block; }
          .action-buttons { gap: 6px !important; }
          .action-icon-btn { width: 32px !important; height: 32px !important; font-size: 13px !important; border-radius: 6px !important; }
        }
        @media (min-width: 769px) {
          .mobile-note-text { display: none !important; }
        }
      `}</style>
    </div>
  );
}
